import { findLastAuthenticatedTheme, getSystemTheme } from "./themePreference";

export type MeetingAppearance = "light" | "dark" | "system";

export interface MeetingMediaPreferences {
  noiseSuppression: boolean;
  echoCancellation: boolean;
  autoGainControl: boolean;
  mirrorVideo: boolean;
  hdVideo: boolean;
  lowBandwidthMode: boolean;
}

export const MEETING_APPEARANCE_KEY = "sprout-video-appearance";
export const MEETING_MEDIA_PREFERENCES_KEY = "sprout-video-media-preferences";
export const MEETING_SPEAKER_DEVICE_KEY = "sprout-video-speaker-device";
export const MEETING_PREFERENCES_EVENT = "sprout-meeting-preferences-changed";

export const DEFAULT_MEETING_MEDIA_PREFERENCES: MeetingMediaPreferences = {
  noiseSuppression: true,
  echoCancellation: true,
  autoGainControl: true,
  mirrorVideo: true,
  hdVideo: true,
  lowBandwidthMode: false,
};

export const MEETING_APPEARANCE_EVENT = "sprout-meeting-appearance-changed";

export const getAppMeetingAppearance = (): "light" | "dark" => {
  const root = document.documentElement;
  const activeTheme = root.getAttribute("data-loader-theme");
  if (activeTheme === "light" || activeTheme === "dark") return activeTheme;
  if (root.classList.contains("dark")) return "dark";
  return findLastAuthenticatedTheme() ?? getSystemTheme();
};

export const getSavedMeetingAppearance = (): MeetingAppearance => {
  if (typeof window === "undefined") return "light";
  try {
    const value = window.localStorage.getItem(MEETING_APPEARANCE_KEY);
    if (value === "dark" || value === "system" || value === "light") return value;
  } catch { /* Fall back to the app theme when storage is unavailable. */ }
  return getAppMeetingAppearance();
};

export const saveMeetingAppearance = (appearance: MeetingAppearance) => {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(MEETING_APPEARANCE_KEY, appearance); } catch { /* Keep the in-memory choice. */ }
  window.dispatchEvent(new Event(MEETING_APPEARANCE_EVENT));
};

export const getSavedMeetingMediaPreferences = (): MeetingMediaPreferences => {
  if (typeof window === "undefined") return DEFAULT_MEETING_MEDIA_PREFERENCES;

  try {
    const raw = window.localStorage.getItem(MEETING_MEDIA_PREFERENCES_KEY);
    if (!raw) return DEFAULT_MEETING_MEDIA_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<MeetingMediaPreferences>;
    return { ...DEFAULT_MEETING_MEDIA_PREFERENCES, ...parsed };
  } catch {
    return DEFAULT_MEETING_MEDIA_PREFERENCES;
  }
};

export const saveMeetingMediaPreferences = (preferences: MeetingMediaPreferences) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MEETING_MEDIA_PREFERENCES_KEY, JSON.stringify(preferences));
  window.dispatchEvent(new CustomEvent(MEETING_PREFERENCES_EVENT, { detail: preferences }));
};

export const getSavedSpeakerDeviceId = (): string => {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(MEETING_SPEAKER_DEVICE_KEY) ?? "";
};

export const saveSpeakerDeviceId = (deviceId: string) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(MEETING_SPEAKER_DEVICE_KEY, deviceId);
};

export const buildAudioConstraint = (
  deviceId: string | undefined,
  preferences: MeetingMediaPreferences,
): MediaTrackConstraints => ({
  ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
  echoCancellation: preferences.echoCancellation,
  noiseSuppression: preferences.noiseSuppression,
  autoGainControl: preferences.autoGainControl,
});

export const buildVideoConstraint = (
  deviceId: string | undefined,
  preferences: MeetingMediaPreferences,
): MediaTrackConstraints => {
  const lowBandwidth = preferences.lowBandwidthMode;
  const useHd = preferences.hdVideo && !lowBandwidth;

  return {
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    width: { ideal: lowBandwidth ? 640 : useHd ? 1280 : 960 },
    height: { ideal: lowBandwidth ? 360 : useHd ? 720 : 540 },
    frameRate: { ideal: lowBandwidth ? 15 : 30, max: lowBandwidth ? 20 : 30 },
  };
};

export const supportsSpeakerSelection = (): boolean =>
  typeof HTMLMediaElement !== "undefined" &&
  typeof (HTMLMediaElement.prototype as HTMLMediaElement & { setSinkId?: (deviceId: string) => Promise<void> }).setSinkId === "function";

export const setAudioOutputDevice = async (element: HTMLMediaElement, deviceId: string) => {
  const sinkElement = element as HTMLMediaElement & { setSinkId?: (nextDeviceId: string) => Promise<void> };
  if (!deviceId || typeof sinkElement.setSinkId !== "function") return;
  await sinkElement.setSinkId(deviceId);
};

export const applySpeakerDeviceToMeetingAudio = async (deviceId: string) => {
  saveSpeakerDeviceId(deviceId);
  if (typeof window === "undefined") return;

  const pool = (window as typeof window & { __sproutAudioPool?: Map<string, HTMLAudioElement> }).__sproutAudioPool;
  if (!pool) return;

  await Promise.all(
    Array.from(pool.values()).map((audioElement) =>
      setAudioOutputDevice(audioElement, deviceId).catch(() => undefined),
    ),
  );
};